import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Pressable, StyleSheet, Text, View } from 'react-native';

import { useHintsEnabled } from './state/hintsPreference';
import type { useTheme } from './theme';

// The generic logger transient-tip primitive (renamed from useEditLockTip
// 2026-07-02 — it long outgrew the edit-lock case). It renders ONE short
// balloon next to the control that raised it, gated on the global Hints
// toggle. Three logger jobs ride on it:
//   1. Edit-lock feedback — a forbidden structural control (add-set, split
//      superset, add dropset step) stays Pressable (NOT `disabled`, which
//      swallows the tap) and, when edit-locked, surfaces a short "why" balloon
//      instead of acting. We grey rather than hide so the edit UI reuses the
//      SAME rendering path as live logging (feedback_edit_mode_reuses_logger_ui).
//   2. The UNI/BI laterality explainer shown each time the chip is switched.
//   3. Burn+ S2 "help mode" — a pinned tip that explains a qualifier chip
//      without firing its action (see useHelpMode).
//
// `showTip(msg, { pinned })`: pinned tips stay until cleared (help mode owns
// the dismissal); un-pinned tips auto-dismiss after DISMISS_MS. Antenore
// 2026-07-02 asked for a longer window than the old 4.5s — a tip you must
// read in one glance is easy to miss.
//
// The global Hints toggle gates only the PASSIVE tips (the auto-balloon that
// appears when you flip a chip). Anything the user ASKED for shows regardless
// of the toggle, otherwise the control would silently do nothing (2026-07-02
// a11y review): that covers both `{ pinned: true }` (help mode) and
// `{ explicit: true }` — a control tapped to reveal a fact, which skips the
// gate but still times out rather than parking on screen (EQ-1, 2026-08-13).
// Pinned tips also render larger (teaching copy, not a one-line confirmation)
// and are tap-to-dismiss.
const DISMISS_MS = 6500;

export function useTransientTip(theme: ReturnType<typeof useTheme>): {
  /** Show the balloon. Un-pinned respects the Hints toggle + auto-dismisses
   *  after DISMISS_MS; `{ pinned: true }` always shows and stays until
   *  clearTip() / a new tip / a tap on the balloon. */
  showTip: (
    message: string,
    opts?: { pinned?: boolean; explicit?: boolean },
  ) => void;
  /** Hide any current tip and cancel a pending auto-dismiss. */
  clearTip: () => void;
  /** The balloon node — render it adjacent to the control(s); null while hidden. */
  tipNode: ReactNode;
} {
  const { enabled: hintsEnabled } = useHintsEnabled();
  const [tip, setTip] = useState<{ text: string; pinned: boolean } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const clearTip = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setTip(null);
  }, []);
  // DS-19 — the non-pinned balloon is `pointerEvents="none"` and auto-dismisses
  // after DISMISS_MS, so TalkBack/VoiceOver never lands on it via touch
  // explore. Announce it the moment it appears instead — `tip.text` is
  // already the translated string the sighted user sees (every callsite
  // passes `t(...)` output into showTip), so this speaks the same copy, never
  // an i18n key. Pinned tips get the same announcement too: it's a harmless,
  // consistent "something appeared" cue on top of their existing focusable
  // Pressable + accessibilityLabel.
  //
  // ONE announcement mechanism on purpose. An accessibilityLiveRegion on the
  // bubble would ALSO fire on Android, so TalkBack would read the same
  // sentence twice; the explicit call covers Android and iOS alike.
  useEffect(() => {
    if (tip) AccessibilityInfo.announceForAccessibility(tip.text);
  }, [tip]);
  const showTip = useCallback(
    (message: string, opts?: { pinned?: boolean; explicit?: boolean }) => {
      const pinned = opts?.pinned === true;
      // Three modes, not two:
      //   passive          → obeys the Hints toggle, auto-dismisses
      //   explicit         → the athlete tapped something to reveal a fact, so
      //                      the Hints toggle does not apply, but it still
      //                      gets out of the way on its own
      //   pinned           → help mode: stays until dismissed
      // The middle one was missing, and EQ-1's machine glyph had to pick
      // between doing nothing when Hints were off and never going away
      // (Antenore on dev 4.15.5: "preferivo che usi la primitiva tooltip
      // auto hide").
      const passive = !pinned && opts?.explicit !== true;
      if (passive && !hintsEnabled) return;
      if (timer.current) clearTimeout(timer.current);
      setTip({ text: message, pinned });
      timer.current = pinned
        ? null
        : setTimeout(() => setTip(null), DISMISS_MS);
    },
    [hintsEnabled],
  );
  const styles = makeStyles(theme);
  if (!tip) return { showTip, clearTip, tipNode: null };
  const bubbleText = (
    <Text style={[styles.bubbleText, tip.pinned && styles.bubbleTextPinned]}>
      {tip.text}
    </Text>
  );
  // Pinned tips are tap-to-dismiss (an explicit exit besides the ? toggle);
  // passive tips stay inert and just time out.
  const tipNode = tip.pinned ? (
    <Pressable
      style={styles.bubble}
      onPress={clearTip}
      accessibilityRole="button"
      accessibilityLabel={tip.text}
    >
      {bubbleText}
    </Pressable>
  ) : (
    <View style={styles.bubble} pointerEvents="none">
      {bubbleText}
    </View>
  );
  return { showTip, clearTip, tipNode };
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Same look as the card logger's HeaderTooltip bubble
    // failBubble: dark bubble, light text, comfortable padding.
    bubble: {
      backgroundColor: p.ink,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      marginBottom: s.s2,
    },
    bubbleText: {
      color: p.bg,
      fontSize: ty.sizes.xs,
      lineHeight: ty.sizes.xs * 1.4,
      fontWeight: '500',
    },
    // Help-mode (pinned) tips carry teaching copy the user reads once, standing,
    // mid-workout — so it renders at `base` (body size), not the xs used for
    // one-line confirmations (2026-07-02 a11y review). Legibility bounds the
    // design (PRODUCT.md); this is content, not chrome.
    bubbleTextPinned: {
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * 1.4,
    },
  });
}
